'use client'

import type { ErrorType } from '@cocoonspace/sdk-js/axios-client/use-custom-instance'
import type { UnauthorizedResponse } from '@cocoonspace/sdk-js/types/unauthorizedResponse'
import type { UnexpectedResponse } from '@cocoonspace/sdk-js/types/unexpectedResponse'
import type { UseQueryResult } from '@tanstack/react-query'
import {
	createContext,
	type PropsWithChildren,
	useContext,
	useEffect,
	useMemo,
} from 'react'
import {
	type getAccount,
	useGetAccount,
} from '@cocoonspace/sdk-js/domains/account/account'
import {
	type getMe,
	useGetMe,
} from '@cocoonspace/sdk-js/domains/profile/profile'
import { useRouter } from 'next/navigation'

import { useFrontChat } from '~/hooks/use-front-chat.hook'
import { useLocale } from '~/hooks/use-locale.hook'
import {
	getLocalizedPath,
	localeCookieName,
	stripLocaleFromPath,
} from '~/i18n/routing'
import { envClient } from '~/lib/env'
import { useAuth } from './auth-provider'
import { useAxeptio } from './axeptio-provider'
import { useTracking } from './tracking-provider'

// biome-ignore lint/complexity/noBannedTypes: explanation
type UserProviderProps = {}

export interface UserContextType {
	userQuery: UseQueryResult<
		Awaited<ReturnType<typeof getMe>>['data'],
		ErrorType<UnauthorizedResponse | UnexpectedResponse>
	>
	accountQuery: UseQueryResult<
		Awaited<ReturnType<typeof getAccount>>['data'],
		ErrorType<UnauthorizedResponse | UnexpectedResponse>
	>
	isIncompleteProfile: boolean
	isInfosPersoIncomplete: boolean
	isInfoFactuIncomplete: boolean
	isEmptyProfile: boolean
	isBusiness: boolean
}

export const UserContext = createContext<UserContextType>({} as UserContextType)

export const useUserContext = () => useContext(UserContext)

export const UserProvider = ({
	children,
}: PropsWithChildren<UserProviderProps>) => {
	const router = useRouter()
	const locale = useLocale()
	const { isAuth, isLoading: isAuthLoading } = useAuth()
	const { analytics } = useTracking()
	const { choices } = useAxeptio()

	const userQuery = useGetMe(undefined, {
		query: {
			enabled: isAuth && !isAuthLoading,
			select: ({ data }) => data,
		},
	})

	const accountQuery = useGetAccount({
		query: {
			enabled: isAuth && !isAuthLoading,
			select: ({ data }) => data,
		},
	})

	const frontChatIdentity =
		isAuth && userQuery.data?.email && userQuery.data?.front_hash
			? {
					email: userQuery.data.email,
					userHash: userQuery.data.front_hash,
				}
			: null

	// Wait for auth; if logged in, wait for profile so verified identity can be passed to init
	const frontChatEnabled = !isAuthLoading && (!isAuth || userQuery.isFetched)

	useFrontChat(envClient.NEXT_PUBLIC_FRONTAPP_CHATID, {
		enabled: frontChatEnabled,
		identity: frontChatIdentity,
	})

	/** Identify mixpanel user */
	useEffect(() => {
		const mixpanelUserId = analytics.user?.().userId

		if (
			userQuery.data?.distinct_id &&
			!mixpanelUserId &&
			userQuery.data?.distinct_id !== mixpanelUserId
		) {
			analytics.identify(userQuery.data?.distinct_id)
		}
	}, [userQuery.data, analytics])

	/** Identify umami user */
	useEffect(() => {
		const analytics_id = userQuery.data?.analytics_id
		const consent = choices?.$$googleConsentMode?.analytics_storage

		if (analytics_id && consent === 'granted') {
			analytics.identify(analytics_id, analytics_id)
		}
	}, [userQuery.data, analytics, choices])

	// Set the locale
	useEffect(() => {
		const userLocale = userQuery.data?.locale
		const isSameLocale = locale === userLocale

		if (!userLocale || isSameLocale) return

		const currentPath = stripLocaleFromPath(window.location.pathname)
		const search = window.location.search
		document.cookie = `${localeCookieName}=${userLocale}; path=/; max-age=31536000; samesite=lax`

		router.push(
			getLocalizedPath(`${currentPath}${search}`, userLocale as typeof locale),
		)
	}, [locale, userQuery.data, router])

	const isInfosPersoIncomplete = useMemo(
		() =>
			!userQuery.data?.email ||
			!userQuery.data?.last_name ||
			!userQuery.data?.first_name ||
			!userQuery.data?.phone,
		[
			userQuery.data?.email,
			userQuery.data?.last_name,
			userQuery.data?.first_name,
			userQuery.data?.phone,
		],
	)

	const isInfoFactuIncomplete = useMemo(() => {
		const billingAddress = accountQuery.data?.billing_address

		return (
			!billingAddress?.city ||
			!billingAddress?.country ||
			!billingAddress?.postal_code ||
			!billingAddress?.address
		)
	}, [accountQuery.data?.billing_address])

	const isIncompleteProfile = useMemo(
		() => isInfosPersoIncomplete || isInfoFactuIncomplete,
		[isInfosPersoIncomplete, isInfoFactuIncomplete],
	)

	const isEmptyProfile = useMemo(() => {
		const user = userQuery.data
		const billingAddress = accountQuery.data?.billing_address

		return (
			!user?.email &&
			!user?.last_name &&
			!user?.first_name &&
			!user?.phone &&
			!billingAddress?.city &&
			!billingAddress?.country &&
			!billingAddress?.postal_code &&
			!billingAddress?.address
		)
	}, [accountQuery.data?.billing_address, userQuery.data])

	const isBusiness = useMemo(
		() => !!accountQuery.data?.business,
		[accountQuery.data?.business],
	)

	const value = useMemo(
		() => ({
			accountQuery,
			isBusiness,
			isEmptyProfile,
			isIncompleteProfile,
			isInfoFactuIncomplete,
			isInfosPersoIncomplete,
			userQuery,
		}),
		[
			userQuery,
			accountQuery,
			isEmptyProfile,
			isInfosPersoIncomplete,
			isInfoFactuIncomplete,
			isIncompleteProfile,
			isBusiness,
		],
	)

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
